import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Issue types (matching municipality app structure)
export enum IssueStatus {
  REPORTED = 'reported',
  ASSIGNED = 'assigned',
  ON_HOLD = 'on_hold',
  IN_PROGRESS = 'in_progress',
  RESOLVED = 'resolved',
  CANCELLED = 'cancelled',
  ARCHIVED = 'archived',
}

export enum IssuePriority {
  URGENT = 'urgent',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export enum IssueCategory {
  ROADS_AND_SIDEWALKS = 'roads_and_sidewalks',
  STREET_LIGHTING = 'street_lighting',
  WATER_SUPPLY = 'water_supply',
  SEWAGE_DRAINAGE = 'sewage_drainage',
  WASTE_MANAGEMENT = 'waste_management',
  PARKS_AND_GREEN_SPACES = 'parks_and_green_spaces',
  PUBLIC_BUILDINGS = 'public_buildings',
  TRAFFIC_SIGNS = 'traffic_signs',
  PARKING = 'parking',
  NOISE_POLLUTION = 'noise_pollution',
  AIR_QUALITY = 'air_quality',
  ANIMAL_CONTROL = 'animal_control',
  GRAFFITI_VANDALISM = 'graffiti_vandalism',
  STREET_CLEANING = 'street_cleaning',
  PUBLIC_SAFETY = 'public_safety',
  BEACH_COASTAL = 'beach_coastal',
  PLAYGROUND_EQUIPMENT = 'playground_equipment',
  PUBLIC_TRANSPORTATION = 'public_transportation',
  ELECTRICAL_ISSUES = 'electrical_issues',
  BUILDING_VIOLATIONS = 'building_violations',
  OTHER = 'other',
}

export interface IssueReporter {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: 'citizen' | 'admin';
}

export interface IssueAssignee {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  municipalityRole: string;
}

export interface Issue {
  id: string;
  municipalityId: string;
  title: string;
  description: string;
  area: string;
  category: IssueCategory;
  status: IssueStatus;
  priority: IssuePriority;
  assignedTo: IssueAssignee[];
  reportedBy: IssueReporter;
  dueDate: Date | null;
  reportedAt: Date;
  createdAt: Date;
  updatedAt: Date;
  resolvedAt: Date | null;
  reopenCount: number;
}

interface IssueStatistics {
  total: number;
  reported: number;
  assigned: number;
  onHold: number;
  inProgress: number;
  resolved: number;
  cancelled: number;
  archived: number;
  urgent: number;
  high: number;
  medium: number;
  low: number;
  overdue: number;
  averageResolutionTime: number; // in hours
}

interface IssueListResponse {
  items: Issue[];
  total: number;
  statistics: IssueStatistics;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to Issue
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): Issue => {
  const docData = data as Record<string, unknown>;
  
  // Convert assignedTo array
  const assignedToData = (docData.assignedTo as Array<Record<string, unknown>>) || [];
  const assignedTo: IssueAssignee[] = assignedToData.map((assignee) => ({
    userId: (assignee.userId as string) || '',
    firstName: (assignee.firstName as string) || '',
    lastName: (assignee.lastName as string) || '',
    email: (assignee.email as string) || '',
    municipalityRole: (assignee.municipalityRole as string) || '',
  }));

  // Convert reportedBy
  const reportedByData = docData.reportedBy as Record<string, unknown>;
  const reportedBy: IssueReporter = {
    userId: (reportedByData.userId as string) || '',
    firstName: (reportedByData.firstName as string) || '',
    lastName: (reportedByData.lastName as string) || '',
    email: (reportedByData.email as string) || '',
    phone: (reportedByData.phone as string) || null,
    role: (reportedByData.role as 'citizen' | 'admin') || 'citizen',
  };

  return {
    id,
    municipalityId,
    title: (docData.title as string) || '',
    description: (docData.description as string) || '',
    area: (docData.area as string) || '',
    category: (docData.category as IssueCategory) || IssueCategory.OTHER,
    status: (docData.status as IssueStatus) || IssueStatus.REPORTED,
    priority: (docData.priority as IssuePriority) || IssuePriority.MEDIUM,
    assignedTo,
    reportedBy,
    dueDate: docData.dueDate ? toDate(docData.dueDate) : null,
    reportedAt: toDate(docData.reportedAt || docData.createdAt),
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    resolvedAt: docData.resolvedAt ? toDate(docData.resolvedAt) : null,
    reopenCount: (docData.reopenCount as number) || 0,
  };
};

export const useMunicipalityIssues = (municipalityId: string | null) => {
  const [data, setData] = useState<IssueListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchIssues = async () => {
      try {
        setLoading(true);
        setError(null);

        const issuesCollection = collection(db, 'municipalities', municipalityId, 'issues');
        const snapshot = await getDocs(query(issuesCollection, orderBy('createdAt', 'desc')));

        const items: Issue[] = [];
        const now = new Date();
        let totalResolutionTime = 0;
        let resolvedCount = 0;
        
        // Status counts
        let reportedCount = 0;
        let assignedCount = 0;
        let onHoldCount = 0;
        let inProgressCount = 0;
        let resolvedCountForStats = 0;
        let cancelledCount = 0;
        let archivedCount = 0;
        
        // Priority counts
        let urgentCount = 0;
        let highCount = 0;
        let mediumCount = 0;
        let lowCount = 0;
        
        // Overdue count
        let overdueCount = 0;

        snapshot.forEach((doc) => {
          const issueItem = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(issueItem);

          // Count by status
          switch (issueItem.status) {
            case IssueStatus.REPORTED:
              reportedCount++;
              break;
            case IssueStatus.ASSIGNED:
              assignedCount++;
              break;
            case IssueStatus.ON_HOLD:
              onHoldCount++;
              break;
            case IssueStatus.IN_PROGRESS:
              inProgressCount++;
              break;
            case IssueStatus.RESOLVED:
              resolvedCountForStats++;
              break;
            case IssueStatus.CANCELLED:
              cancelledCount++;
              break;
            case IssueStatus.ARCHIVED:
              archivedCount++;
              break;
          }

          // Count by priority
          switch (issueItem.priority) {
            case IssuePriority.URGENT:
              urgentCount++;
              break;
            case IssuePriority.HIGH:
              highCount++;
              break;
            case IssuePriority.MEDIUM:
              mediumCount++;
              break;
            case IssuePriority.LOW:
              lowCount++;
              break;
          }

          // Check if overdue (not resolved/cancelled and past due date)
          if (
            issueItem.dueDate &&
            issueItem.status !== IssueStatus.RESOLVED &&
            issueItem.status !== IssueStatus.CANCELLED &&
            new Date(issueItem.dueDate) < now
          ) {
            overdueCount++;
          }

          // Calculate resolution time for resolved issues
          if (issueItem.resolvedAt && issueItem.reportedAt) {
            const resolutionTimeMs = issueItem.resolvedAt.getTime() - issueItem.reportedAt.getTime();
            const resolutionTimeHours = resolutionTimeMs / (1000 * 60 * 60);
            totalResolutionTime += resolutionTimeHours;
            resolvedCount++;
          }
        });

        const total = items.length;
        const averageResolutionTime = resolvedCount > 0 ? Math.round(totalResolutionTime / resolvedCount) : 0;

        setData({
          items,
          total,
          statistics: {
            total,
            reported: reportedCount,
            assigned: assignedCount,
            onHold: onHoldCount,
            inProgress: inProgressCount,
            resolved: resolvedCountForStats,
            cancelled: cancelledCount,
            archived: archivedCount,
            urgent: urgentCount,
            high: highCount,
            medium: mediumCount,
            low: lowCount,
            overdue: overdueCount,
            averageResolutionTime,
          },
        });
      } catch (err) {
        console.error('Error fetching municipality issues:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality issues');
      } finally {
        setLoading(false);
      }
    };

    fetchIssues();
  }, [municipalityId]);

  return { data, loading, error };
};
